
import React, { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fetchPropertyById } from "@/api/property";
import AccountingTab from "./property-settings/AccountingTab";
import PmsTab from "./property-settings/PmsTab";
import UsersTab from "./property-settings/UsersTab";
import EmployeesTab from "./property-settings/EmployeesTab";
import ForecastingTab from "./property-settings/ForecastingTab";
import { AvailableIntegration, getAvailableIntegrations, getPropertyIntegrations, Integration } from "@/api/IntegrationsAPI";

const PropertySettings = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [availableIntegrations, setAvailableIntegrations] = useState<AvailableIntegration[]>([]);
  const [pmsAvailableIntegrations, setPmsAvailableIntegrations] = useState<AvailableIntegration[]>([]);
  const [accIntegration, setAccIntegration] = useState<Integration | null>(null);
  const [pmsIntegration, setPmsIntegration] = useState<Integration | null>(null);

  // Fetch integrations and set state
  const fetchIntegrations = async () => {
    try {
      // Fetch accounting integrations
      const accIntegrations = await getAvailableIntegrations("acc", id || "");
      // console.log("Available Accounting Integrations: ", accIntegrations);
      setAvailableIntegrations(accIntegrations);
      
      // Fetch PMS integrations
      const pmsIntegrations = await getAvailableIntegrations("pms", id || "");
      // console.log("Available PMS Integrations: ", pmsIntegrations);
      setPmsAvailableIntegrations(pmsIntegrations);

      const currentIntegrations: Integration[] = await getPropertyIntegrations(id || "");
      console.log("Current Integrations: ", currentIntegrations);

      if (currentIntegrations.length > 0) {
        // Set the accounting integration based on type "acc"
        const accountingIntegration = currentIntegrations.find((integration) => integration.type === "acc");
        if (accountingIntegration) {
          setAccIntegration(accountingIntegration);
        }

        // Set the PMS integration based on type "pms"
        const pmsIntegration = currentIntegrations.find((integration) => integration.type === "pms");
        if (pmsIntegration) {
          setPmsIntegration(pmsIntegration);
        }

        console.log("Accounting Integration: ", accountingIntegration);
        console.log("PMS Integration: ", pmsIntegration);
      }

      toast({
        title: "Success",
        description: "Available integrations fetched successfully",
      });
    } catch (error) {
      console.error("Error fetching available integrations: ", error);
      toast({
        title: "Error",
        description: "Failed to fetch available integrations.",
        variant: "destructive",
      });
    }
  };

  // Call fetchIntegrations when the component mounts
  useEffect(() => {
    const loadIntegrations = async () => {
      if ((availableIntegrations.length === 0 || pmsAvailableIntegrations.length === 0) && id) {
        console.log("useEffect triggered with id:", id);
        await fetchIntegrations();
      }
    };
    
    loadIntegrations();
  }, [id]);

  // Fetch property data using fetchPropertyById
  const { data: hotel, isLoading, error } = useQuery({
    queryKey: ["property", id],
    queryFn: () => fetchPropertyById(id || ""),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[70vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !hotel) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-red-500">Error loading property</h1>
        <p>Could not load property settings. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{hotel.body.name} - Settings</h1>
        <p className="text-muted-foreground">
          Configure your property integrations and settings
        </p>
      </div>

      <Tabs defaultValue="accounting" className="space-y-6">
          <TabsList>
            <TabsTrigger value="accounting">Accounting</TabsTrigger>
            <TabsTrigger value="pms">Property Management System</TabsTrigger>
            {(() => {
              // Determine admin status from various possible locations
              const isAdmin = !!(
                sessionStorage.getItem("isAdmin") === "true"
              );
              return isAdmin ? <TabsTrigger value="users">Users</TabsTrigger> : null;
            })()}
            <TabsTrigger value="employees">Employees</TabsTrigger>
            <TabsTrigger value="forecasting">Forecasting</TabsTrigger>
          </TabsList>

        {/* Accounting Settings Tab */}
        <TabsContent value="accounting" className="space-y-6">
          <AccountingTab
            propertyId={id || ""}
            currentIntegration={accIntegration}
            availableIntegrations={availableIntegrations}
            onDeleteIntegration={() => {
              setAccIntegration(null);
            }
            }
          />
        </TabsContent>

        {/* Property Management System Tab */}
        <TabsContent value="pms" className="space-y-6">
          <PmsTab
            propertyId={id || ""}
            currentIntegration={pmsIntegration}
            availableIntegrations={pmsAvailableIntegrations}
          />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <UsersTab propertyId={id || ""} />
        </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees" className="space-y-6">
            <EmployeesTab propertyId={id || ""} currentIntegration={accIntegration} />
          </TabsContent>
          {/* Forecasting Tab */}
          <TabsContent value="forecasting" className="space-y-6">
            <ForecastingTab propertyId={id || ""} />
          </TabsContent>
      </Tabs>
    </div>
  );
};

export default React.memo(PropertySettings);
